// 텐서 (design 02 3.1, 3.4, 8.1): the workbook's tensors in one list.
//   - built in: 지출[사람, 월, 분류], 수입[사람, 월, 분류], 예산[월, 분류]
//     (from the money table and the budgets)
//   - input tensors: numbers people type, over the built-in dimensions and
//     the ones they made; edited here as a table: two dimensions as rows
//     and columns, the others fixed to one item each (/tensors/<id>)
//   - computed tensors: defined names (=수식), shown with the dimensions
//     of their result. Before one is saved the engine calculates it on this
//     device (the server keeps formulas as typed and does not read them): a
//     #DIM! or a circular reference is not saved (tensors.ts checkName).
// Formulas use them all by name: =SUM(용돈[사람="엄마"]), sum(목표, over=분류).
import { type ClipboardEvent, type FormEvent, type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react'

import { errorText } from '../../api/client'
import { isComposing } from '../../lib/ime'
import { ErrorText, Field, Icon, IconButton, Sheet, Spinner } from '../../ui/common'
import { navigate } from '../../ui/router'
import { showToast } from '../../ui/toast'
import type { Id, TensorDef, TensorDimKey, TensorEntry, TensorName } from '../api'
import { customDims, identProblem } from '../dims'
import { engine } from '../engine'
import { monthOf } from '../format'
import { monthRange } from '../grid/builtins'
import { nextPlace } from '../grid/entry'
import { getState } from '../store'
import type { GridStore } from '../grid/store'
import { parseTsv } from '../grid/tsv'
import { useGrid } from '../grid/useGrid'
import {
  BUILTIN_DIMS,
  COORD_SEP,
  type NameCheck,
  type Place,
  type Shape,
  cellText,
  checkName,
  dimKeyName,
  editorMonths,
  entryMonths,
  inputTensors,
  parseCellNumber,
  placesOf,
  shapeOf,
  shapeText,
  tensorTitle,
} from '../tensors'
import type { Current } from './common'
import { type Choice, FormulaGuide, FuncPicker, formulaNames, putInto } from './FormulaHelp'

const BUILT_IN = [
  { name: '지출', dims: '[사람, 월, 분류]', note: '거래 표의 지출을 대분류로 모은 것' },
  { name: '수입', dims: '[사람, 월, 분류]', note: '거래 표의 수입' },
  { name: '예산', dims: '[월, 분류]', note: '예산 화면에서 정한 것 (대분류)' },
]

/** The names of dimensions whose order is only for showing (not for shift). */
function categoryDimNames(cur: Pick<Current, 'book'>): string[] {
  return ['사람', '분류', '결제수단', ...customDims(cur.book?.dims).filter((d) => d.kind === 'category').map((d) => d.name)]
}

/**
 * The checks of a computed tensor before it is saved (design 02 3.4), for a
 * name of the store's workbook: null when it may be saved, else why not.
 * `except`: the name being changed.
 */
export async function nameRefusal(store: GridStore, name: string, formula: string, except = 0): Promise<string | null> {
  const book = getState().books[store.workbookId]
  const c = await checkName(
    { name, formula },
    store.model.nameList().filter((n) => n.id !== except),
    categoryDimNames({ book }),
    (f) => store.evaluate(f),
  )
  return c.ok ? null : c.message
}

/** The engine's answer for each defined name (its result's dimensions). */
export function useShapes(store: GridStore, names: TensorName[]): Map<string, Shape> | null {
  const [shapes, setShapes] = useState<Map<string, Shape> | null>(null)
  const key = names.map((n) => `${n.name}\u0000${n.formula}`).join('\n')
  useEffect(() => {
    let live = true
    const t = setTimeout(() => {
      store
        .evaluate(names.map((n) => '=' + n.name))
        .then((answers) => {
          if (!live) return
          const m = new Map<string, Shape>()
          names.forEach((n, i) => {
            const a = answers[i]
            m.set(n.name, a?.ok ? shapeOf(a.value) : { kind: 'error', error: '#NAME?' })
          })
          setShapes(m)
        })
        .catch(() => live && setShapes(new Map()))
    }, 120)
    return () => {
      live = false
      clearTimeout(t)
    }
    // names follow key; the store's version is a change of its data
  }, [store, store.version, key])
  return shapes
}

// ---- computed tensors (defined names) ----

export function NameEditor(props: {
  cur: Current
  store: GridStore
  name: TensorName | null
  onDone: () => void
}) {
  const { cur, store } = props
  const [name, setName] = useState(props.name?.name ?? '')
  const [formula, setFormula] = useState(props.name ? '=' + props.name.formula : '=')
  const [check, setCheck] = useState<NameCheck | null>(null)
  const [busy, setBusy] = useState(false)
  // the guide and the functions (FormulaHelp.tsx); their buttons leave the focus in the formula
  const box = useRef<HTMLTextAreaElement>(null)
  const [caret, setCaret] = useState(0)
  const [picking, setPicking] = useState(false)
  const nameList = store.model.nameList()
  const names = useMemo(() => formulaNames(cur.book, nameList), [cur.book, nameList])
  const keep = (e: React.PointerEvent) => e.preventDefault()
  const put = (c: Choice) => {
    const el = box.current
    if (!el) return
    el.focus()
    setFormula(putInto(el, c))
    setCaret(el.selectionStart)
    setCheck(null)
    setPicking(false)
  }

  const run = () =>
    checkName(
      { name: name.trim(), formula },
      store.model.nameList().filter((n) => n.id !== props.name?.id),
      categoryDimNames(cur),
      (f) => store.evaluate(f),
    )

  async function test() {
    setBusy(true)
    try {
      setCheck(await run())
    } catch (e) {
      setCheck({ ok: false, message: errorText(e), shape: null })
    } finally {
      setBusy(false)
    }
  }

  async function save(e: FormEvent) {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    try {
      const c = await run()
      setCheck(c)
      if (!c.ok) return
      const f = formula.trim().replace(/^=/, '')
      if (props.name) {
        const body: { name?: string; formula?: string } = {}
        if (name.trim() !== props.name.name) body.name = name.trim()
        if (f !== props.name.formula) body.formula = f
        if (Object.keys(body).length) await store.updateName(props.name.id, body)
      } else {
        await store.addName(name.trim(), f)
      }
      props.onDone()
    } catch (err) {
      setCheck({ ok: false, message: errorText(err), shape: null })
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="t-form" onSubmit={(e) => void save(e)}>
      <Field label="이름" hint="수식에서 쓰는 이름 (예: 가족지출)">
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={255} autoComplete="off" />
      </Field>
      <Field label="수식" hint="예: =sum(지출, over=사람), =예산 - sum(지출, over=사람)">
        <textarea
          ref={box}
          className="t-formula"
          value={formula}
          rows={2}
          spellCheck={false}
          onChange={(e) => {
            setFormula(e.target.value)
            setCaret(e.target.selectionStart)
            setCheck(null)
          }}
          onSelect={(e) => setCaret(e.currentTarget.selectionStart)}
        />
      </Field>
      <div className="t-formula-tools">
        <button
          type="button"
          className={`g-fx${picking ? ' on' : ''}`}
          aria-label="함수"
          aria-pressed={picking}
          title="함수"
          onPointerDown={keep}
          onClick={() => setPicking(!picking)}
        >
          <i>fx</i>
        </button>
        <span className="muted small">함수와 이름을 골라 넣습니다</span>
      </div>
      {picking && <FuncPicker names={names} onPick={put} onClose={() => setPicking(false)} keep={keep} />}
      <FormulaGuide text={formula} caret={caret} names={names} onPick={put} keep={keep} />
      {check && (
        <p className={check.ok ? 'muted' : 'error'} role="status">
          {check.shape && check.shape.kind !== 'error' && (
            <>
              결과: <strong className="t-shape">{shapeText(check.shape)}</strong>{' '}
            </>
          )}
          {check.message}
        </p>
      )}
      <p className="muted small">저장하기 전에 이 기기에서 계산해 차원을 확인합니다. 차원이 맞지 않거나(#DIM!) 순환 참조면 저장하지 않습니다.</p>
      <div className="t-buttons">
        <button type="button" className="secondary" disabled={busy} onClick={() => void test()}>
          검사
        </button>
        <button type="submit" className="primary grow" disabled={busy || name.trim() === '' || formula.trim().replace(/^=/, '') === ''}>
          저장
        </button>
      </div>
    </form>
  )
}

function ComputedList({ cur, store }: { cur: Current; store: GridStore }) {
  const names = store.model.nameList()
  const shapes = useShapes(store, names)
  const [editing, setEditing] = useState<TensorName | null | undefined>(undefined)
  return (
    <section className="card">
      <h3>계산 텐서</h3>
      <p className="muted small">이름 정의(=수식)입니다. 표 화면의 이름 정의와 같습니다.</p>
      {store.status === 'loading' && <Spinner />}
      <ul className="list t-tensors">
        {names.map((n) => {
          const s = shapes?.get(n.name)
          return (
            <li key={n.id} className="t-tensor">
              <button type="button" className="t-tensor-main" onClick={() => setEditing(n)}>
                <span className="t-tensor-name">{n.name}</span>
                <span className={`t-shape${s?.kind === 'error' ? ' danger' : ''}`}>{s ? shapeText(s) : '…'}</span>
                <code className="t-tensor-formula">={n.formula}</code>
              </button>
              <IconButton
                label={`${n.name} 지우기`}
                onClick={() => {
                  if (confirm(`'${n.name}'을(를) 지울까요? 이 이름을 쓰던 수식은 #NAME?이 됩니다.`))
                    void store.deleteName(n.id).catch((e) => showToast(errorText(e)))
                }}
              >
                <Icon name="trash" size={18} />
              </IconButton>
            </li>
          )
        })}
      </ul>
      {names.length === 0 && store.status === 'ready' && <p className="muted">아직 없습니다.</p>}
      <button type="button" className="secondary" onClick={() => setEditing(null)}>
        새 계산 텐서
      </button>
      <Sheet open={editing !== undefined} onClose={() => setEditing(undefined)} title={editing ? `계산 텐서 ${editing.name}` : '새 계산 텐서'}>
        {editing !== undefined && (
          <NameEditor key={editing?.id ?? 0} cur={cur} store={store} name={editing} onDone={() => setEditing(undefined)} />
        )}
      </Sheet>
    </section>
  )
}

// ---- input tensors ----

function NewTensorSheet({ cur, open, onClose }: { cur: Current; open: boolean; onClose: () => void }) {
  const dims = customDims(cur.book?.dims)
  const [name, setName] = useState('')
  const [unit, setUnit] = useState('원')
  const [keys, setKeys] = useState<TensorDimKey[]>(['month'])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const choices: { key: TensorDimKey; label: string }[] = [
    ...BUILTIN_DIMS.map((d) => ({ key: d.key, label: d.key === 'category' ? '분류 (대분류)' : d.name })),
    ...dims.map((d) => ({ key: `d${d.id}` as TensorDimKey, label: d.name })),
  ]
  const toggle = (k: TensorDimKey) => setKeys((ks) => (ks.includes(k) ? ks.filter((x) => x !== k) : [...ks, k]))

  async function submit(e: FormEvent) {
    e.preventDefault()
    const taken = [...inputTensors(cur.book?.tensors).map((t) => t.name), ...dims.map((d) => d.name)]
    const why = identProblem(name, taken)
    if (why) return setError(why)
    if (keys.length === 0) return setError('차원을 하나 이상 고르세요.')
    setBusy(true)
    setError(null)
    try {
      const ordered = choices.map((c) => c.key).filter((k) => keys.includes(k))
      const t = await engine.addTensor(cur.workbook.id, { name: name.trim(), dims: ordered, unit: unit.trim() })
      setName('')
      onClose()
      navigate(`/tensors/${t.id}`)
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title="새 텐서">
      <form className="t-form" onSubmit={(e) => void submit(e)}>
        <Field label="이름" hint="수식에서 쓰는 이름 (예: 용돈, 목표)">
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={30} autoComplete="off" />
        </Field>
        <fieldset className="t-dim-pick">
          <legend>차원 (만든 뒤에는 바꿀 수 없어요)</legend>
          {choices.map((c) => (
            <label key={c.key} className="radio-choice">
              <input type="checkbox" checked={keys.includes(c.key)} onChange={() => toggle(c.key)} /> {c.label}
            </label>
          ))}
        </fieldset>
        <Field label="단위" hint="예: 원, %, kg (비워도 됩니다)">
          <input value={unit} onChange={(e) => setUnit(e.target.value)} maxLength={10} />
        </Field>
        <ErrorText text={error} />
        <button type="submit" className="primary" disabled={busy || name.trim() === ''}>
          만들기
        </button>
      </form>
    </Sheet>
  )
}

function TensorSettings({ cur, tensor, onClose }: { cur: Current; tensor: TensorDef; onClose: () => void }) {
  const [name, setName] = useState(tensor.name)
  const [unit, setUnit] = useState(tensor.unit)
  const [error, setError] = useState<string | null>(null)

  async function save(e: FormEvent) {
    e.preventDefault()
    const body: { name?: string; unit?: string; version: number } = { version: tensor.version }
    if (name.trim() !== tensor.name) {
      const taken = [
        ...inputTensors(cur.book?.tensors)
          .filter((t) => t.id !== tensor.id)
          .map((t) => t.name),
        ...customDims(cur.book?.dims).map((d) => d.name),
      ]
      const why = identProblem(name, taken)
      if (why) return setError(why)
      body.name = name.trim()
    }
    if (unit.trim() !== tensor.unit) body.unit = unit.trim()
    if (body.name === undefined && body.unit === undefined) return onClose()
    try {
      await engine.updateTensor(cur.workbook.id, tensor.id, body)
      onClose()
    } catch (err) {
      setError(errorText(err))
    }
  }

  async function remove() {
    if (!confirm(`텐서 '${tensor.name}'와 그 값을 모두 지울까요? 이 이름을 쓰던 수식은 #NAME?이 됩니다.`)) return
    try {
      await engine.deleteTensor(cur.workbook.id, tensor.id)
      onClose()
      navigate('/tensors', { replace: true })
    } catch (err) {
      setError(errorText(err))
    }
  }

  return (
    <form className="t-form" onSubmit={(e) => void save(e)}>
      <Field label="이름" hint="이름을 바꿔도 수식은 저절로 고쳐지지 않습니다.">
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={30} />
      </Field>
      <Field label="단위">
        <input value={unit} onChange={(e) => setUnit(e.target.value)} maxLength={10} />
      </Field>
      <ErrorText text={error} />
      <div className="t-buttons">
        <button type="button" className="danger-btn" onClick={() => void remove()}>
          텐서 지우기
        </button>
        <button type="submit" className="primary grow">
          저장
        </button>
      </div>
    </form>
  )
}

/** The months a tensor's 월 offers here: the workbook's and a year ahead. */
function useMonths(cur: Current): string[] {
  const book = cur.book
  return useMemo(() => {
    const months: string[] = [monthOf(Date.now())]
    for (const r of cur.rows) months.push(r.day.slice(0, 7))
    for (const b of Object.values(book?.budgets ?? {})) if (b.amount > 0) months.push(b.month)
    months.push(...entryMonths(inputTensors(book?.tensors), book?.entries))
    return editorMonths(monthRange(months), monthOf(Date.now()))
  }, [cur.rows, book])
}

interface Layout {
  row: number
  col: number // -1: a single column
  fixed: Record<number, string> // dimension index -> part
}

/** A cell typed into an input tensor (the editor here, a saved view). */
export interface TypedCell {
  coord: string
  text: string
  entry: TensorEntry | undefined
}

/**
 * Sends typed cells of an input tensor, 5000 at a time; a text that is not
 * a number stops them all. `pending` marks the cells on the way.
 */
export async function sendEntries(
  workbookId: Id,
  tensorId: Id,
  cells: TypedCell[],
  pending: (coords: string[], on: boolean) => void,
): Promise<void> {
  const body: { coord: string; value: number | null; version: number }[] = []
  for (const c of cells) {
    const v = parseCellNumber(c.text)
    if (v === undefined) {
      showToast(`수가 아닙니다: ${c.text}`)
      return
    }
    if ((c.entry?.value ?? null) === v) continue
    body.push({ coord: c.coord, value: v, version: c.entry?.version ?? 0 })
  }
  if (body.length === 0) return
  const coords = body.map((b) => b.coord)
  pending(coords, true)
  try {
    for (let i = 0; i < body.length; i += 5000) await engine.setTensorCells(workbookId, tensorId, body.slice(i, i + 5000))
  } catch (e) {
    showToast(errorText(e))
  } finally {
    pending(coords, false)
  }
}

/** Marks coords pending (a Set in state). */
export function markPending(set: (f: (s: Set<string>) => Set<string>) => void): (coords: string[], on: boolean) => void {
  return (coords, on) =>
    set((s) => {
      const n = new Set(s)
      for (const c of coords) {
        if (on) n.add(c)
        else n.delete(c)
      }
      return n
    })
}

/** The box after el among its table's number boxes (before it with back):
    down the column, then on at the top of the next one (grid/entry.ts). */
function nextBox(el: HTMLInputElement, back = false): HTMLInputElement | null {
  const boxes = Array.from(el.closest('table')?.querySelectorAll<HTMLInputElement>('input.t-entry[data-r]') ?? [])
  const place = (b: HTMLInputElement) => ({ r: Number(b.dataset.r), c: Number(b.dataset.c) })
  const i = nextPlace(boxes.map(place), place(el), back)
  return i >= 0 ? boxes[i]! : null
}

/**
 * One cell of the editor: typed text while it has the focus. Enter (a
 * phone keyboard's 다음) writes it and goes to the next box down the
 * column, then to the top of the next column, the keyboard staying up;
 * from the last one (완료) the keyboard goes. Shift+Enter goes back.
 */
export function EntryCell(props: {
  entry: TensorEntry | undefined
  label: string
  onCommit: (text: string, entry: TensorEntry | undefined) => Promise<void>
  onPaste: (text: string) => boolean
  pending: boolean
  /** its row and column in the table */
  r: number
  c: number
}) {
  const { entry } = props
  const shown = entry && entry.value !== null ? cellText(entry.value) : ''
  const [draft, setDraftState] = useState<string | null>(null)
  // as it is now: Enter writes, then the blur of going on finds it written
  const draftRef = useRef<string | null>(null)
  const setDraft = (d: string | null) => {
    draftRef.current = d
    setDraftState(d)
  }
  const commit = () => {
    const text = draftRef.current
    if (text === null) return
    setDraft(null)
    if (text.trim() === shown.trim()) return
    void props.onCommit(text, entry)
  }
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setDraft(null)
      e.currentTarget.blur()
      return
    }
    // Enter while a syllable is being composed is not the end (design 8.7)
    if (e.key !== 'Enter' || isComposing(e)) return
    e.preventDefault()
    commit()
    const next = nextBox(e.currentTarget, e.shiftKey)
    if (next) next.focus()
    else e.currentTarget.blur()
  }
  return (
    <input
      className={`t-entry${props.pending ? ' pending' : ''}`}
      aria-label={props.label}
      inputMode="decimal"
      autoComplete="off"
      data-r={props.r}
      data-c={props.c}
      value={draft ?? shown}
      onFocus={(e) => {
        const el = e.currentTarget
        setDraft(entry && entry.value !== null ? String(entry.value) : '')
        el.select()
        // the keyboard's key, before it comes up: 다음, or 완료 on the last box
        el.enterKeyHint = nextBox(el) ? 'next' : 'done'
      }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={onKey}
      onPaste={(e: ClipboardEvent<HTMLInputElement>) => {
        const text = e.clipboardData.getData('text/plain')
        if (/[\t\n]/.test(text.trim()) && props.onPaste(text)) {
          e.preventDefault()
          setDraft(null)
          e.currentTarget.blur()
        }
      }}
    />
  )
}

function TensorEditor({ cur, tensor }: { cur: Current; tensor: TensorDef }) {
  const items = cur.book?.items ?? {}
  const months = useMonths(cur)
  const places = useMemo(
    () => tensor.dims.map((k) => placesOf(k, items, cur.book?.dims, months)),
    [tensor.dims, items, cur.book?.dims, months],
  )
  const names = tensor.dims.map((k) => dimKeyName(k, cur.book?.dims) ?? '?')
  const monthAt = tensor.dims.indexOf('month')
  const [layout, setLayout] = useState<Layout>(() => {
    const n = tensor.dims.length
    const row = monthAt >= 0 ? monthAt : 0
    const col = n < 2 ? -1 : row === 0 ? 1 : 0
    return { row, col, fixed: {} }
  })
  const [settings, setSettings] = useState(false)
  const [pending, setPending] = useState<Set<string>>(() => new Set())
  const entries = cur.book?.entries[tensor.id] ?? {}
  const gone = places.some((p) => p === null)

  // the fixed dimensions: the item picked, or the first (this month for 월)
  const fixedPart = (d: number): string | undefined => {
    const list = places[d] ?? []
    const want = layout.fixed[d]
    if (want !== undefined && list.some((p) => p.part === want)) return want
    if (d === monthAt) {
      const now = monthOf(Date.now())
      if (list.some((p) => p.part === now)) return now
    }
    return list[0]?.part
  }
  const coordAt = (rowPart: string, colPart: string | null): string | null => {
    const parts: string[] = []
    for (let d = 0; d < tensor.dims.length; d++) {
      const p = d === layout.row ? rowPart : d === layout.col ? colPart : fixedPart(d)
      if (p === undefined || p === null) return null
      parts.push(p)
    }
    return parts.join(COORD_SEP)
  }

  const send = (cells: TypedCell[]) => sendEntries(cur.workbook.id, tensor.id, cells, markPending(setPending))

  if (gone)
    return (
      <div className="page t-page">
        <p className="muted empty-note">이 텐서의 차원 중 지워진 것이 있습니다.</p>
      </div>
    )
  const rows: Place[] = places[layout.row] ?? []
  const cols: (Place | null)[] = layout.col >= 0 ? (places[layout.col] ?? []) : [null]
  const otherDims = tensor.dims.map((_, d) => d).filter((d) => d !== layout.row && d !== layout.col)

  const paste = (r0: number, c0: number) => (text: string) => {
    const grid = parseTsv(text)
    const cells: TypedCell[] = []
    grid.forEach((line, i) =>
      line.forEach((t, j) => {
        const rp = rows[r0 + i]
        const cp = cols[c0 + j]
        if (!rp || cp === undefined) return
        const coord = coordAt(rp.part, cp?.part ?? null)
        if (coord) cells.push({ coord, text: t, entry: entries[coord] })
      }),
    )
    void send(cells)
    return true
  }

  return (
    <div className="page t-page t-wide-page t-tensor-editor">
      <div className="t-editor-head">
        <IconButton label="텐서 목록" onClick={() => navigate('/tensors')}>
          <Icon name="back" />
        </IconButton>
        <h3 className="grow">
          {tensorTitle(tensor, cur.book?.dims)}
          {tensor.unit && <span className="muted small"> ({tensor.unit})</span>}
        </h3>
        <IconButton label="텐서 설정" onClick={() => setSettings(true)}>
          <Icon name="gear" />
        </IconButton>
      </div>
      <div className="t-layout" role="group" aria-label="배치">
        <label>
          행
          <select
            value={layout.row}
            onChange={(e) => {
              const row = Number(e.target.value)
              setLayout((l) => ({ ...l, row, col: l.col === row ? l.row : l.col }))
            }}
          >
            {names.map((n, d) => (
              <option key={d} value={d}>
                {n}
              </option>
            ))}
          </select>
        </label>
        {tensor.dims.length > 1 && (
          <label>
            열
            <select
              value={layout.col}
              onChange={(e) => {
                const col = Number(e.target.value)
                setLayout((l) => ({ ...l, col, row: l.row === col ? l.col : l.row }))
              }}
            >
              {names.map((n, d) => (
                <option key={d} value={d}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        )}
        {otherDims.map((d) => (
          <label key={d}>
            {names[d]}
            <select value={fixedPart(d) ?? ''} onChange={(e) => setLayout((l) => ({ ...l, fixed: { ...l.fixed, [d]: e.target.value } }))}>
              {(places[d] ?? []).map((p) => (
                <option key={p.part} value={p.part}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>
      {rows.length === 0 || cols.length === 0 ? (
        <p className="muted empty-note">이 차원에 항목이 없습니다. 항목·차원 관리에서 더해 주세요.</p>
      ) : (
        <div className="t-scroll">
          <table className="t-table t-entries">
            <thead>
              <tr>
                <th>
                  {names[layout.row]}↓{layout.col >= 0 ? ` ${names[layout.col]}→` : ''}
                </th>
                {cols.map((c) => (
                  <th key={c?.part ?? '-'} className="num">
                    {c?.label ?? '값'}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.part}>
                  <th scope="row">{r.label}</th>
                  {cols.map((c, j) => {
                    const coord = coordAt(r.part, c?.part ?? null)
                    if (!coord) return <td key={c?.part ?? '-'} />
                    return (
                      <td key={c?.part ?? '-'} className="num">
                        <EntryCell
                          entry={entries[coord]}
                          label={`${r.label}${c ? ` ${c.label}` : ''}`}
                          r={i}
                          c={j}
                          pending={pending.has(coord)}
                          onCommit={(text, entry) => send([{ coord, text, entry }])}
                          onPaste={paste(i, j)}
                        />
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="muted small">
        빈칸은 값 없음입니다. 엑셀에서 복사한 칸을 붙여 넣을 수 있습니다. 수식에서: <code>=SUM({tensor.name}[{names[layout.row]}=…])</code>
      </p>
      <Sheet open={settings} onClose={() => setSettings(false)} title="텐서 설정">
        {settings && <TensorSettings key={tensor.version} cur={cur} tensor={tensor} onClose={() => setSettings(false)} />}
      </Sheet>
    </div>
  )
}

function TensorList({ cur }: { cur: Current }) {
  const store = useGrid(cur.workbook.id)
  const [making, setMaking] = useState(false)
  const tensors = inputTensors(cur.book?.tensors)
  return (
    <div className="page t-page t-wide-page">
      <section className="card">
        <h3>내장 텐서</h3>
        <ul className="list t-tensors">
          {BUILT_IN.map((b) => (
            <li key={b.name} className="t-tensor">
              <span className="t-tensor-main">
                <span className="t-tensor-name">{b.name}</span>
                <span className="t-shape">{b.dims}</span>
                <span className="muted small">{b.note}</span>
              </span>
            </li>
          ))}
        </ul>
      </section>
      <section className="card">
        <h3>입력 텐서</h3>
        <p className="muted small">직접 적는 수입니다. 사람·월·분류·결제수단과 만든 차원 위에 둡니다.</p>
        {cur.book?.loading && tensors.length === 0 && <Spinner />}
        <ul className="list t-tensors">
          {tensors.map((t) => {
            const n = Object.values(cur.book?.entries[t.id] ?? {}).filter((e) => e.value !== null).length
            return (
              <li key={t.id} className="t-tensor">
                <button type="button" className="t-tensor-main" onClick={() => navigate(`/tensors/${t.id}`)}>
                  <span className="t-tensor-name">{t.name}</span>
                  <span className="t-shape">[{t.dims.map((k) => dimKeyName(k, cur.book?.dims) ?? '?').join(', ')}]</span>
                  <span className="muted small">
                    값 {n.toLocaleString('ko-KR')}칸{t.unit ? ` · ${t.unit}` : ''}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
        <button type="button" className="secondary" onClick={() => setMaking(true)}>
          새 텐서
        </button>
      </section>
      <ComputedList cur={cur} store={store} />
      <NewTensorSheet cur={cur} open={making} onClose={() => setMaking(false)} />
    </div>
  )
}

/** /tensors and /tensors/<id> */
export function TensorsScreen({ cur, path }: { cur: Current; path: string }) {
  const m = /^\/tensors\/(\d+)$/.exec(path)
  const id: Id = m ? Number(m[1]) : 0
  const tensor = id ? cur.book?.tensors[id] : undefined
  if (id && tensor && !tensor.deleted) return <TensorEditor key={tensor.id} cur={cur} tensor={tensor} />
  if (id && !cur.book?.loading)
    return (
      <div className="page t-page">
        <p className="muted empty-note">없는 텐서입니다.</p>
        <button type="button" className="secondary" onClick={() => navigate('/tensors', { replace: true })}>
          텐서 목록
        </button>
      </div>
    )
  if (id) return <Spinner />
  return <TensorList cur={cur} />
}
